// AI Features Configuration - Tiered Access (75%, 20%, 5%)

export const AI_FEATURES_CONFIG = {
  free: {
    smartReplies: {
      enabled: true,
      limit: 10, // 10 per day
      name: 'Smart Replies',
      description: 'Generate quick responses',
      tier: 'FREE'
    },
    summarize: {
      enabled: true,
      limit: 5, // 5 per day
      maxMessages: 50, // Max 50 messages to summarize
      name: 'Basic Summarize',
      description: 'Short conversation summaries',
      tier: 'FREE'
    },
    qa: {
      enabled: true,
      limit: 5, // 5 per day
      maxMessages: 20, // Only recent 20 messages
      name: 'Basic Q&A',
      description: 'Ask about recent messages',
      tier: 'FREE'
    },
    translate: {
      enabled: false,
      tier: 'PREMIUM'
    },
    sentiment: {
      enabled: false,
      tier: 'PREMIUM'
    },
    actionItems: {
      enabled: false,
      tier: 'PREMIUM'
    },
    suggestions: {
      enabled: false,
      tier: 'ENTERPRISE'
    }
  },
  premium: {
    smartReplies: {
      enabled: true,
      limit: 100, // 100 per day
      name: 'Smart Replies',
      description: 'Generate quick responses',
      tier: 'PREMIUM'
    },
    summarize: {
      enabled: true,
      limit: 50, // 50 per day
      maxMessages: 1000, // Up to 1000 messages
      name: 'Advanced Summarize',
      description: 'Full conversation summaries',
      tier: 'PREMIUM'
    },
    translate: {
      enabled: true,
      limit: 50, // 50 per day
      name: 'Translate',
      description: 'Multi-language translation',
      tier: 'PREMIUM'
    },
    sentiment: {
      enabled: true,
      limit: 30, // 30 per day
      name: 'Sentiment Analysis',
      description: 'Analyze conversation tone',
      tier: 'PREMIUM'
    },
    actionItems: {
      enabled: true,
      limit: 30, // 30 per day
      name: 'Action Items',
      description: 'Extract tasks & todos',
      tier: 'PREMIUM'
    },
    qa: {
      enabled: true,
      limit: 50, // 50 per day
      maxMessages: 500, // Up to 500 messages
      name: 'Advanced Q&A',
      description: 'Ask about conversation',
      tier: 'PREMIUM'
    },
    suggestions: {
      enabled: false,
      tier: 'ENTERPRISE'
    }
  },
  enterprise: {
    smartReplies: {
      enabled: true,
      limit: -1, // Unlimited
      name: 'Smart Replies',
      description: 'Generate quick responses',
      tier: 'ENTERPRISE'
    },
    summarize: {
      enabled: true,
      limit: -1, // Unlimited
      maxMessages: -1, // Unlimited
      name: 'Enterprise Summarize',
      description: 'Unlimited conversation summaries',
      tier: 'ENTERPRISE'
    },
    translate: {
      enabled: true,
      limit: -1, // Unlimited
      name: 'Enterprise Translate',
      description: 'Unlimited translation',
      tier: 'ENTERPRISE'
    },
    sentiment: {
      enabled: true,
      limit: -1, // Unlimited
      name: 'Enterprise Sentiment',
      description: 'Unlimited sentiment analysis',
      tier: 'ENTERPRISE'
    },
    actionItems: {
      enabled: true,
      limit: -1, // Unlimited
      name: 'Enterprise Action Items',
      description: 'Unlimited task extraction',
      tier: 'ENTERPRISE'
    },
    qa: {
      enabled: true,
      limit: -1, // Unlimited
      maxMessages: -1, // Unlimited
      name: 'Enterprise Q&A',
      description: 'Unlimited Q&A',
      tier: 'ENTERPRISE'
    },
    suggestions: {
      enabled: true,
      limit: -1, // Unlimited
      name: 'Advanced Suggestions',
      description: 'Strategic AI recommendations',
      tier: 'ENTERPRISE'
    },
    customModels: true,
    priorityProcessing: true,
    apiAccess: true
  }
};

// Tier colors for badges
export const TIER_COLORS = {
  FREE: 'bg-green-600',
  PREMIUM: 'bg-cyan-600',
  ENTERPRISE: 'bg-purple-600'
};

// Check if user has access to a feature
export const hasFeatureAccess = (userTier, featureName) => {
  const tierConfig = AI_FEATURES_CONFIG[userTier];
  if (!tierConfig) return false;
  
  const feature = tierConfig[featureName];
  return feature && feature.enabled;
};

// Get feature config for user's tier
export const getFeatureConfig = (userTier, featureName) => {
  const tierConfig = AI_FEATURES_CONFIG[userTier];
  if (!tierConfig) return null;
  
  return tierConfig[featureName] || null;
};

// Get required tier for a feature
export const getRequiredTier = (featureName) => {
  // Check which tier has this feature enabled
  if (AI_FEATURES_CONFIG.free[featureName]?.enabled) return 'free';
  if (AI_FEATURES_CONFIG.premium[featureName]?.enabled) return 'premium';
  if (AI_FEATURES_CONFIG.enterprise[featureName]?.enabled) return 'enterprise';
  return 'enterprise'; // Default to enterprise for unknown features
};

export default AI_FEATURES_CONFIG;
